import { ColDef, RowClickedEvent, RowDoubleClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, onGridReady } from "src/utils/grid/gridStyleUtils";
import { formatDate } from "src/utils/string/stringUtil";
import { useSAL6020_R01Store } from "./Store_SAL6020_R01";
import { gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";

import { defaultSAL6020_R01Dto, SAL6020_R01HeaderDto } from "./Dto_SAL6020_R01";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import useUiuxStore from "src/store/useUiuxStore";
import { getMenuComponent } from "src/utils/menu/menuUtil";
import { cellDateComparator, onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";

const COLUMN_STATE_KEY = 'SAL6020_R01-grid-column-state';
// 숫자 컬럼 목록 (헤더 DTO 키 보장 필요 없으므로 문자열 배열로 유지)
const numberFields: string[] = ['salAmt'];

interface Props {
	gridRef: React.RefObject<AgGridReact<SAL6020_R01HeaderDto> | null>;
	handleCreateClick?: () => void;
}

export const Table_SAL6020_R01 = (props: Props) => {

	const { gridRef, handleCreateClick } = props;

	const { SAL6020_R01List = [], 
		selectedSAL6020_R01, setSelectedSAL6020_R01, initSelectedSAL6020_R01,
		columnState, setColumnState, 
		SAL6020_R01DetailList = [], setSAL6020_R01DetailList,
		scrollTop, setScrollTop,
		setDetailScrollTop,
	} = useSAL6020_R01Store() as any;

	const { addTabList, setActiveTab, setSAL6020_SAL6010NavigationData } = useUiuxStore();

	//const gridRef = useRef<AgGridReact>(null);
	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const { 
		getRowId,
		settingRowIndex, 
		settingSecondColumnCount,
		createRowClassRules,
		paddedGridData,
		renderIndexCell,
		defaultRowIndexCellButton,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		ensureSelectedRow,
	} = useGridSettingContent<SAL6020_R01HeaderDto>({
		gridRef,
		key: 'salesEstimateNo',
		gridData: SAL6020_R01List,
		defaultGridData: defaultSAL6020_R01Dto,
		selectedRow: selectedSAL6020_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: "",
		compNm: '회사명',
		estimateDt: '견적일자',
		customNm: '거래처명',
		salAmt: '매출금액',
	}

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'compCd')(params), []);

	const baseColumnDefs: ColDef<SAL6020_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '사업장', colId: 'compNm', field: 'compNm', width: 119,
			cellStyle: (p) => getCellStyle(p, 'compNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '견적일자', colId: 'estimateDt', field: 'estimateDt', width: 107,
			cellStyle: (p) => getCellStyle(p, 'salDt'),
			headerClass: aggridHeaderClass,
			valueFormatter: (p: { data?: SAL6020_R01HeaderDto }) =>
				p.data?.estimateDt ? formatDate(new Date(p.data.estimateDt)) : '',
			comparator: cellDateComparator,
		},
		{
			headerName: '거래처명', colId: 'customNm', field: 'customNm', width: 180,
			cellStyle: (p) => getCellStyle(p, 'customNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '화폐구분', colId: 'moneyCd', field: 'moneyCd', width: 180,
			cellStyle: (p) => getCellStyle(p, 'moneyCd'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '부가세구분', colId: 'vatType', field: 'vatType', width: 180,
			cellStyle: (p) => getCellStyle(p, 'vatType'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '견적금액', colId: 'estimateAmt', field: 'estimateAmt', width: 118,
			cellStyle: (p) => getCellStyle(p, 'estimateAmt'),
			headerClass: aggridHeaderClass,
			comparator: (valueA, valueB) => valueA - valueB,
			valueFormatter: (params) => params.value?.toLocaleString() ?? '',
		},
		{
			headerName: '부가세액', colId: 'vatAmt', field: 'vatAmt', width: 180,
			cellStyle: (p) => getCellStyle(p, 'vatAmt'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '견적 유효기간 ', colId: 'dueDt', field: 'dueDt', width: 180,
			cellStyle: (p) => getCellStyle(p, 'customNm'),
			headerClass: aggridHeaderClass,
		},
		{
			headerName: '비고', colId: 'remark', field: 'remark', width: 180,
			cellStyle: (p) => getCellStyle(p, 'remark'),
			headerClass: aggridHeaderClass,
		},
	], [getCellStyle, cellDateComparator]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<SAL6020_R01HeaderDto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<SAL6020_R01HeaderDto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);


	// 마운트 시 컬럼 상태 복원
	
	const handleRowClick = useCallback((e: RowClickedEvent<SAL6020_R01HeaderDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		
		// 선택된 표와 정보가 같은지 확인 - salesEstimateNo를 우선 사용
		const currentKey = rowData.salesEstimateNo || rowData.salBillNo;
		const selectedKey = selectedSAL6020_R01?.salesEstimateNo || selectedSAL6020_R01?.salBillNo;
		const isSelected = selectedKey === currentKey;
		
		if (isSelected) {
			setSelectedSAL6020_R01(undefined);
			setDetailScrollTop(null);
			handleCreateClick && handleCreateClick();
		} else {
			setDetailScrollTop(null);
			setSelectedSAL6020_R01(rowData);
		}
	}, [selectedSAL6020_R01, setSelectedSAL6020_R01, setDetailScrollTop, handleCreateClick]);

	// 더블클릭으로 SAL6010 페이지로 이동
	const handleRowDoubleClick = useCallback((e: RowDoubleClickedEvent<SAL6020_R01HeaderDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		// 네비게이션 데이터 설정 (견적번호만 전달)
		setSAL6020_SAL6010NavigationData({
			salesEstimateNo: rowData.salesEstimateNo
		});

		// SAL6010_R01 탭 추가
		const sal6010Component = getMenuComponent('SAL6010_R01');
		if (sal6010Component) {
			const newTab = {
				key: 'SAL6010_R01',
				title: '견적서 등록',
				component: sal6010Component
			};
			
			addTabList(newTab);
			setActiveTab(newTab);
		}
	}, [setSAL6020_SAL6010NavigationData, addTabList, setActiveTab]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		compCd: SAL6020_R01List.length.toLocaleString() + "건",
		totalAmt: SAL6020_R01List.reduce((sum: number, item: SAL6020_R01HeaderDto) => (sum + (item.totalAmt ?? 0)), 0).toLocaleString(),
		editable: false,
	}]), [SAL6020_R01List]);

	useEffect(() => { ensureSelectedRow(); }, [SAL6020_R01List, selectedSAL6020_R01]);

	return (
		
		<GridContextWrapper<SAL6020_R01HeaderDto>
			enableCellCalculation
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedSAL6020_R01}
			defaultRowData={defaultSAL6020_R01Dto}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<SAL6020_R01HeaderDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				
				rowClassRules={createRowClassRules<SAL6020_R01HeaderDto>({
					idKey: 'salesEstimateNo',
					selectedList: selectedSAL6020_R01?.salesEstimateNo ? [selectedSAL6020_R01.salesEstimateNo] : [],
					deletedList: [],
					
				})}
				onRowClicked={handleRowClick}
				onRowDoubleClicked={handleRowDoubleClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}	
				
				// Grid_Select 공통 로직 처리를 위한 props 전달
				setHoveredRowIndex={setHoveredRowIndex}		//마우스 오버 상태 이벤트를 위한 핸들러
				columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 (로컬스토리)	
				setColumnState={setColumnState}			//컬럼 상태 저장
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	)
}
